'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import { useIsClient } from '@/hooks';

/**
 * Props for `SideContentFeatureList`.
 */
export type SideContentFeatureListProps =
  SliceComponentProps<Content.SideContentFeatureListSlice>;

/**
 * Component for "SideContentFeatureList" Slices.
 * Displays a numbered list with descriptions on the left and an image on the right.
 */
const SideContentFeatureList: FC<SideContentFeatureListProps> = ({ slice }) => {
  const { title, intro, items, visual } = slice.primary;
  const isWithChecks = slice.variation === 'withChecks';

  const contentRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const isClient = useIsClient();

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const contentEl = contentRef.current;
    const imageEl = imageRef.current;

    if (contentEl) {
      contentEl.style.transitionDelay = '0ms';
      observer.observe(contentEl);
    }

    if (imageEl) {
      imageEl.style.transitionDelay = '200ms';
      observer.observe(imageEl);
    }

    return () => {
      if (contentEl) {
        observer.unobserve(contentEl);
      }
      if (imageEl) {
        observer.unobserve(imageEl);
      }
    };
  }, [isClient]);

  // Render withChecks variation
  if (isWithChecks) {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="py-[80px] px-5 md:px-16 bg-[#041118]"
      >
        <div className="max-w-[1040px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-[60px] items-center">
            {/* Left Content - Title, Intro, and List */}
            <div
              ref={contentRef}
              className="flex-1 lg:w-[520px] flex flex-col gap-6 opacity-0 translate-y-8 transition-all duration-700 ease-out"
            >
              {/* Title */}
              {isFilled.richText(title) && (
                <PrismicRichText
                  field={title}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-white">
                        {children}
                      </h2>
                    ),
                  }}
                />
              )}

              {/* Intro */}
              {isFilled.richText(intro) && (
                <PrismicRichText
                  field={intro}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-lg font-normal leading-[1.5] text-white">
                        {children}
                      </p>
                    ),
                    strong: ({ children }) => (
                      <span className="font-semibold">{children}</span>
                    ),
                  }}
                />
              )}

              {/* List Items with Check Icons */}
              {items && items.length > 0 && (
                <div className="flex flex-col gap-0">
                  {items.map((item, index) => (
                    <div
                      key={index}
                      className="border-l border-[#d7ede3] pl-6 py-2 flex flex-col gap-2"
                    >
                      {/* Check Icon + Label */}
                      {item.label && (
                        <div className="flex items-center gap-2">
                          {/* Check Icon */}
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="flex-shrink-0"
                          >
                            <path
                              d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
                              fill="#05f273"
                            />
                          </svg>
                          <p className="text-xl font-semibold leading-[1.4] text-[#05f273]">
                            {item.label}
                          </p>
                        </div>
                      )}

                      {/* Description */}
                      {isFilled.richText(item.description) && (
                        <PrismicRichText
                          field={item.description}
                          components={{
                            paragraph: ({ children }) => (
                              <p className="text-lg font-normal leading-[1.4] text-white">
                                {children}
                              </p>
                            ),
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Content - Visual/Image */}
            {isFilled.image(visual) && (
              <div
                ref={imageRef}
                className="flex-1 lg:w-[667px] shrink-0 opacity-0 translate-y-8 transition-all duration-700 ease-out"
              >
                <PrismicNextImage
                  field={visual}
                  className="w-full h-auto object-contain"
                />
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Default variation (image_right_list_text_left)
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-[80px] px-4 md:px-8 lg:px-[200px]"
      style={{ backgroundColor: '#0e1010' }}
    >
      <div className="max-w-[1140px] mx-auto">
        <div className="flex flex-col lg:flex-row gap-[60px] items-center">
          {/* Left Content - Title, Intro, and List */}
          <div
            ref={contentRef}
            className="flex-1 lg:w-[520px] flex flex-col gap-6 opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {/* Title */}
            {isFilled.richText(title) && (
              <PrismicRichText
                field={title}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-white">
                      {children}
                    </h2>
                  ),
                }}
              />
            )}

            {/* Intro */}
            {isFilled.richText(intro) && (
              <PrismicRichText
                field={intro}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-lg font-normal leading-[1.5] text-white">
                      {children}
                    </p>
                  ),
                }}
              />
            )}

            {/* Numbered List Items */}
            {items && items.length > 0 && (
              <div className="flex flex-col gap-0">
                {items.map((item, index) => (
                  <div
                    key={index}
                    className="border-l border-[#d7ede3] pl-6 py-2 flex flex-col gap-2"
                  >
                    {/* Numbered Label */}
                    {item.label && (
                      <p className="text-xl font-semibold leading-[1.4] text-[#05f273]">
                        {index + 1}. {item.label}
                      </p>
                    )}

                    {/* Description */}
                    {isFilled.richText(item.description) && (
                      <PrismicRichText
                        field={item.description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="text-lg font-normal leading-[1.4] text-white">
                              {children}
                            </p>
                          ),
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Content - Visual/Image */}
          {isFilled.image(visual) && (
            <div
              ref={imageRef}
              className="flex-1 lg:w-[459px] shrink-0 opacity-0 translate-y-8 transition-all duration-700 ease-out"
            >
              <PrismicNextImage
                field={visual}
                className="w-full h-auto object-contain"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SideContentFeatureList;
